import React from "react";

import cx from "classnames";

import { getWithDefault } from "@kazoohr/helpers";

import { Flex } from "../Flex";
import { IconButton } from "../IconButton";
import { Select } from "../Select";
import { Text } from "../Text";
import { UtilityText } from "../UtilityText";

import styles from "./Pagination.scss";

const defaultPageCountOptions = [
  { label: "50", value: "50" },
  { label: "100", value: "100" },
  { label: "200", value: "200" },
];

/**
 * Props for the Pagination component.
 */
export interface PaginationProps {
  /**
   * CSS class name that is attached to the wrapper div
   */
  className?: string;
  /**
   * Callback executed when the page is changed, it is passed a number of the page requested
   */
  onPage: (page: number) => void;
  /**
   * Callback executed when the quantity is changed, it is passed a string of the new quantity selected
   */
  onPageCountChange?: (quantity: string) => void;
  /**
   * The current page being viewed
   */
  current: number;
  /**
   * The total pages available
   */
  total: number;
  /**
   * The currently selected items per a page, this should be a value key inside pageCountOptions
   */
  selectedPageQuantity?: string;
  /**
   * Array of options for Select component
   */
  pageCountOptions?: Array<{ label: string; value: string }>;
  /**
   * Label text for the left side of pagination
   */
  paginationLabelLeft?: string;
  /**
   * Label text for the right side of pagination
   */
  paginationLabelRight?: string;
  /**
   * ID to be added to wrapper div and `-select` will be appended and passed down to select
   */
  id?: string;
  /**
   * Indicates if the quantity selection should be shown / rendered, defaults to `false`
   */
  showPageCount?: boolean;
  /**
   * Data test ID for testing purposes
   */
  "data-testid"?: string;
  /**
   * Whether to use foggy pagination (shows "-" instead of total)
   */
  foggyPagination?: boolean;
}

/**
 * Pagination component that provides navigation controls for paging through results.
 * It uses callbacks to execute paging and changing items per page, so it can easily
 * be tied back to an API.
 */
export function Pagination({
  current,
  total = 1,
  onPage,
  className = "",
  onPageCountChange,
  pageCountOptions = defaultPageCountOptions,
  selectedPageQuantity = defaultPageCountOptions[0].value,
  paginationLabelLeft,
  paginationLabelRight,
  foggyPagination = false,
  id,
  showPageCount,
  "data-testid": testId = "",
}: PaginationProps) {
  const wrapperClass = cx(className, styles.wrapper);
  const buttonClass = cx(styles.controls, styles.buttons);
  const lastPage = foggyPagination ? "-" : total.toString();

  const defaultOnPage = () => {};
  const defaultOnPageCountChange = () => {};
  const safeOnPage = getWithDefault(onPage, defaultOnPage);
  const safeOnPageCountChange = getWithDefault(
    onPageCountChange,
    defaultOnPageCountChange
  );

  return (
    <div className={wrapperClass} id={id} data-testid={testId}>
      {showPageCount && (
        <Flex alignItems="center" justifyContent="center">
          <div
            className={styles.select}
            data-testid="pagination-quantity-selector"
          >
            <Select
              compact={true}
              onChange={(option) => safeOnPageCountChange(option.value)}
              options={pageCountOptions}
              selectedOptionValue={selectedPageQuantity}
              id={`${id}-select`}
              renderPopoverInPortal={false}
            />
          </div>
          {paginationLabelLeft && (
            <Text className={styles.text} size="small">
              {paginationLabelLeft}
            </Text>
          )}
        </Flex>
      )}
      <div className={styles.buttonGroup}>
        {paginationLabelRight && (
          <UtilityText size="small">{paginationLabelRight}</UtilityText>
        )}
        <IconButton
          icon="caret_left"
          variant="transparent"
          className={buttonClass}
          size="extra_small"
          onClick={() => safeOnPage(current - 1)}
          disabled={current <= 1}
          data-testid="pagination-previous-page-button"
        />
        <Text
          data-testid="pagination-text"
          size="small"
          color={styles.asphalt}
          className={styles.controls}
        >
          {`${current} / ${lastPage}`}
        </Text>
        <IconButton
          icon="caret_right"
          variant="transparent"
          className={buttonClass}
          size="extra_small"
          onClick={() => safeOnPage(current + 1)}
          disabled={total <= current}
          data-testid="pagination-next-page-button"
        />
      </div>
    </div>
  );
}
